import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';

import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { ModalService } from '@app/core/ui/components/modal';
import {
  ModalData,
  MODAL_DATA
} from '@app/core/ui/components/modal/modal.component';
import { ToastService } from '@app/core/ui/components/toast';
import { SafeTransformPipe } from '@app/shared/pipes/safe-transform.pipe';

import { environment } from '@env/environment';

import { BemobiCheckoutMessage } from '../../models/bemobi-checkout-message.model';
import { BemobiCheckoutWebhookService } from '../../services/bemobi-checkout-webhook.service';
import { ToggleCartDrawerAction } from '../../store/cart/cart-ui.actions';
import { ClearCartAction } from '../../store/cart/cart.action';
import { ResetLoadingAction } from '../../store/cart-checkout/cart-checkout.actions';
import {
  extractBemobiErrorCode,
  isBemobiSessionLoadError,
  resolveBemobiCheckoutErrorMessage
} from '../../utils/bemobi-checkout-error.util';
import { buildBemobiCheckoutIframeUrl } from '../../utils/bemobi-checkout-url.util';

const DEFAULT_IFRAME_HEIGHT = 560;
const CHECKOUT_LOAD_TIMEOUT_MS = 30_000;

@Component({
  selector: 'mw-bemobi-checkout-modal',
  imports: [SafeTransformPipe],
  templateUrl: './bemobi-checkout-modal.component.html',
  styleUrl: './bemobi-checkout-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BemobiCheckoutModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly modal = inject(ModalService);
  private readonly webhookService = inject(BemobiCheckoutWebhookService);
  private readonly toastService = inject(ToastService);
  private readonly data =
    inject<ModalData<{ sessionToken: string }>>(MODAL_DATA);

  protected readonly iframeHeight = signal(DEFAULT_IFRAME_HEIGHT);
  protected readonly isIframeLoading = signal(true);

  protected readonly sessionToken = computed(
    () => this.data?.sessionToken ?? ''
  );

  protected readonly iframeUrl = computed(() => {
    const sessionToken = this.sessionToken();

    if (!sessionToken) {
      return '';
    }

    return buildBemobiCheckoutIframeUrl(
      environment.checkout.bemobi.iframeBaseUrl,
      sessionToken
    );
  });

  private iframeLoadTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private iframeDocumentLoaded = false;
  private shouldRestoreCartOnDestroy = true;
  private isHandlingSuccess = false;

  ngOnInit(): void {
    const sessionToken = this.sessionToken();

    if (!sessionToken) {
      this.handleCheckoutLoadFailure();
      return;
    }

    this.startIframeLoadTimeout();

    this.webhookService.startListening((message) => {
      void this.handleWebhookEvent(message);
    });
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.webhookService.stopListening();

    if (this.shouldRestoreCartOnDestroy) {
      this.restoreCartAfterCheckoutFailure();
    }
  }

  protected onIframeLoad(): void {
    this.iframeDocumentLoaded = true;
    this.isIframeLoading.set(false);
    this.clearIframeLoadTimeout();
  }

  private startIframeLoadTimeout(): void {
    this.iframeLoadTimeoutId = setTimeout(() => {
      if (this.iframeDocumentLoaded) {
        return;
      }

      this.handleCheckoutLoadFailure();
    }, CHECKOUT_LOAD_TIMEOUT_MS);
  }

  private handleCheckoutLoadFailure(errorCode?: string): void {
    if (this.isHandlingSuccess) {
      return;
    }

    this.clearTimeouts();
    this.toastService.danger(
      resolveBemobiCheckoutErrorMessage(null, errorCode, 'load')
    );
    this.restoreCartAfterCheckoutFailure();
    this.shouldRestoreCartOnDestroy = false;
    this.modal.close();
  }

  private handleCheckoutError(
    payload: unknown,
    errorCode?: string,
    phase: 'load' | 'payment' = 'payment'
  ): void {
    // Pagamento já concluído: ignorar erros tardios do iframe (ex.: hide/close).
    if (this.isHandlingSuccess) {
      return;
    }

    this.isIframeLoading.set(false);
    this.clearTimeouts();
    this.toastService.danger(
      resolveBemobiCheckoutErrorMessage(payload, errorCode, phase)
    );
    this.restoreCartAfterCheckoutFailure();
    this.shouldRestoreCartOnDestroy = false;
    this.modal.close();
  }

  private restoreCartAfterCheckoutFailure(): void {
    this.store.dispatch([
      new ResetLoadingAction(),
      new ToggleCartDrawerAction(true)
    ]);
  }

  private clearIframeLoadTimeout(): void {
    if (this.iframeLoadTimeoutId === null) {
      return;
    }

    clearTimeout(this.iframeLoadTimeoutId);
    this.iframeLoadTimeoutId = null;
  }

  private clearTimeouts(): void {
    this.clearIframeLoadTimeout();
  }

  private async handleCheckoutSuccess(): Promise<void> {
    if (this.isHandlingSuccess) {
      return;
    }

    this.isHandlingSuccess = true;
    this.isIframeLoading.set(false);
    this.clearIframeLoadTimeout();
    this.shouldRestoreCartOnDestroy = false;

    try {
      await firstValueFrom(
        this.store.dispatch([new ClearCartAction(), new ResetLoadingAction()])
      );
    } catch {
      // Pagamento já concluído; falha ao limpar o carrinho não deve reabrir o fluxo.
    }
  }

  private async handleWebhookEvent(
    message: BemobiCheckoutMessage
  ): Promise<void> {
    switch (message.type) {
      case 'checkout:resize':
        this.isIframeLoading.set(false);
        this.clearIframeLoadTimeout();
        this.iframeHeight.set(message.height);
        break;
      case 'checkout:success':
        await this.handleCheckoutSuccess();
        break;
      case 'checkout:error': {
        const errorCode =
          message.errorCode ?? extractBemobiErrorCode(message.payload);
        const phase = isBemobiSessionLoadError(errorCode) ? 'load' : 'payment';

        this.handleCheckoutError(
          message.payload,
          errorCode ?? undefined,
          phase
        );
        break;
      }
      case 'checkout:close':
        if (this.isHandlingSuccess || !this.shouldRestoreCartOnDestroy) {
          this.modal.close();
          break;
        }

        this.isIframeLoading.set(false);
        this.clearIframeLoadTimeout();
        this.restoreCartAfterCheckoutFailure();
        this.shouldRestoreCartOnDestroy = false;
        this.modal.close();
        break;
      case 'checkout:action':
        this.isIframeLoading.set(false);
        this.clearIframeLoadTimeout();
        break;
      default:
        break;
    }
  }
}
